/**
 * Quién ve la tarjeta **OED · Cobros** del Cuadro de mandos y la pantalla del Centro de Cobros
 * (#187), en un solo sitio.
 *
 * **D-COB-5** (dirección, 04/10/2026): cada comercial ve su cartera; cada gestor de cobro, la suya
 * y las facturas sin gestor asignado; **Administración, Directores y Administrador ven todo**.
 *
 * POR QUÉ ESTO NO ES `permisos-facturacion.js`. Son dos permisos distintos y confundirlos rompe uno
 * de los dos:
 *
 *  - La **pestaña FACTURACIÓN** es una restricción: solo ADMIN y DIRECTOR (D-ROL). Son las cifras
 *    de la empresa y los objetivos de dirección.
 *  - El **Centro de Cobros** no niega la entrada: **recorta**. Si se gateara con la lista de
 *    FACTURACIÓN, el comercial —que es justo para quien la pantalla está hecha— se quedaría fuera.
 *
 * Y POR QUÉ HAY LISTA, SI RECORTA. Porque la rejilla del Cuadro de mandos tiene ocho tarjetas y una
 * que dijera «0 €» a quien no tiene ninguna factura asignada —un inspector, por ejemplo— sería
 * ruido, no información. La lista son los roles que D-COB-5 nombra.
 *
 * LO QUE QUEDA ABIERTO: «cada gestor» no es un rol del sistema, es quien figure como PGC en alguna
 * factura, y eso el navegador no lo sabe sin preguntar.
 *
 * **Y desde la #188 ya puede pasar.** Cuando se escribió esto, ninguna de las 928 facturas
 * pendientes tenía gestor asignado, así que el hueco era teórico. La #188 asigna, de modo que a
 * partir de ahora puede haber un gestor de cobro con cartera y sin ninguno de estos cuatro roles: la
 * API le contestaría su cartera y la bandeja sin asignar —el recorte de D-COB-5 se la da—, pero esta
 * lista no le enseña ni la tarjeta ni la entrada, así que no llegaría a pedirla. Hoy sigue sin
 * doler, porque quien gestiona cobros son dos personas de Administración o Dirección y entran por
 * rol; si algún día se le asigna cartera a alguien de fuera de esa lista, la salida no es adivinarlo
 * aquí —la definición de gestor es un dato, no un rol (D-COB-3)— sino preguntarle al servidor, que
 * ya lo resuelve. Está dicho en el PR y en la incidencia de la #188, para que lo decida Dirección
 * con el caso delante y no de pasada.
 *
 * **Lo que la #188 sí hizo** fue dejar su propia pantalla —la bandeja de asignación,
 * `/dashboard/cobros/asignacion/`— **sin esta barrera**: allí el recorte lo aplica la API y es ella
 * la que dice en `alcance` qué parte se está viendo, así que ese gestor sin rol, si tiene el enlace,
 * entra y ve lo suyo. El porqué entero está en el comentario de esa ruta en `routes.js`. Lo que
 * sigue abierto es esto de aquí: la tarjeta y la entrada al Centro de Cobros.
 *
 * La lista NO se toca en la #188: es una decisión de la #187 y el invariante del programa dice que
 * ningún permiso existente se cambia salvo donde la incidencia lo pida.
 *
 * El recorte de verdad lo aplica la API en la propia consulta y la respuesta dice en `alcance` qué
 * parte se está viendo. Esto solo decide si se enseña la entrada.
 */
const ROLES_ENTRADA = ['ADMIN', 'DIRECTOR', 'ADMINISTRACION', 'COMERCIAL'];

/**
 * Quién ve el informe **Abonos abiertos** (COB-7, #193), que es otra pregunta.
 *
 * **La misma decisión D-COB-5, pero su otra mitad.** Arriba es «quién entra en el Centro de Cobros»,
 * que es cualquiera porque la cartera se recorta. Esto es «quién ve TODO», que es lo único que tiene
 * sentido en este informe: los abonos abiertos son de la empresa entera y no hay una parte que sea
 * de un comercial. El criterio 5 de la incidencia lo dice al revés y es lo mismo: «a un comercial no
 * le aparece».
 *
 * Así que aquí el permiso **no recorta, cierra**: el servidor contesta 403 a quien no esté en esta
 * lista (`AuthzService.canVerTodaLaCartera`, que es el mismo método con el que decide la cartera). No
 * se ha creado ningún permiso nuevo, que es lo que pide la incidencia: es el mismo de siempre
 * preguntado para otra pantalla.
 *
 * Y por eso **no hay COMERCIAL** en esta lista y sí en la de arriba. Si alguien las unifica «porque
 * son del mismo módulo», al comercial le aparece un enlace que lleva a un 403, que es peor que no
 * tener enlace: hace creer que le han quitado permisos.
 */
const ROLES_INFORME_ABONOS = ['ADMIN', 'DIRECTOR', 'ADMINISTRACION'];

const PermisosCobros = {
    ROLES_ENTRADA: ROLES_ENTRADA,

    ROLES_INFORME_ABONOS: ROLES_INFORME_ABONOS,

    /**
     * Si ese usuario ve la entrada al Centro de Cobros. **Fail-closed**: sin usuario, sin roles o
     * con otros roles, no.
     *
     * Recibe el usuario en vez de buscarlo, por lo mismo que `permisos-facturacion.js`: quien
     * pregunta decide con cuál. La ruta pasa el que ha traído `Usuario.me()` en esta carga y la
     * tarjeta del Cuadro de mandos pasa el mismo, para que no puedan contestar cosas distintas.
     *
     * @param {Object} usuario el usuario, con su `roles`.
     */
    verCentroCobros: function(usuario) {
        return tieneAlguno(usuario, ROLES_ENTRADA);
    },

    /**
     * Si ese usuario ve el informe **Abonos abiertos** (COB-7, #193). **Fail-closed**, igual que
     * arriba.
     *
     * La contestan **los dos sitios que tienen que coincidir**: la ruta del informe, que decide si
     * se monta la pantalla, y la ruta del Centro de Cobros, que le pasa la respuesta a
     * `centro-cobros.vue` para que dibuje —o no— el enlace. Con la lista copiada en la pantalla, el
     * enlace y la página podían decir cosas distintas; es el fallo que la revisión de este repo ha
     * sacado tres veces (PR #1707, #1711 y #1735).
     *
     * @param {Object} usuario el usuario, con su `roles`.
     */
    verInformeAbonos: function(usuario) {
        return tieneAlguno(usuario, ROLES_INFORME_ABONOS);
    }
};

/** Si el usuario tiene alguno de esos roles. Sin usuario y sin roles, no. */
function tieneAlguno(usuario, permitidos) {
    const roles = (usuario && usuario.roles) || [];
    return permitidos.some(function(rol) { return roles.indexOf(rol) > -1; });
}

export default PermisosCobros;
